import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Play, Loader2, X, Square, History } from 'lucide-react';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import { AgentStatusDot, agentStatusLabel } from 'features/agents/lib/status';

const primaryButtonClass =
  'inline-flex h-8 items-center gap-1.5 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand whitespace-nowrap transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-50';
const pressed = 'bg-surface-hover';

/**
 * The editor's top bar: where you are, what state the agent is in, and the
 * few things you can do to it. Test, Versions and Runs are the neutral
 * toolbar buttons used across the app; Publish (Save, for an unsaved draft)
 * is the one primary.
 */
export default function EditorTopBar({
  team, workflowId, isDraft, workflow,
  nameDraft, onNameChange, onNameBlur, onSaveNew, creating,
  previewVersion, onExitPreview,
  savingState, savedFlash, hasUnpublishedChanges,
  historyOpen, onToggleHistory,
  testActive, runningManual, hasNodes, onTest, onStopTest,
  publishing, blocking, onPublish,
}) {
  const published = !!workflow?.published_version_id;
  const base = team ? `/${encodeURIComponent(team)}` : '';

  let note = null;
  if (previewVersion) note = `Viewing v${previewVersion.version_number}`;
  else if (isDraft) note = 'Not saved yet';
  else if (savingState === 'saving') note = 'Saving…';
  else if (savingState === 'dirty') note = 'Unsaved changes';
  else if (savedFlash) note = 'Saved';
  else if (hasUnpublishedChanges && published) note = 'Unpublished changes';

  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] bg-surface px-4">
      <Link to={team ? `${base}/agents` : '/'} className="shrink-0 whitespace-nowrap text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]">Agents</Link>
      <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden="true" />
      <input
        type="text"
        aria-label="Agent name"
        value={nameDraft}
        onChange={(e) => { if (!previewVersion) onNameChange(e.target.value); }}
        onBlur={previewVersion ? undefined : onNameBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') { if (isDraft) onSaveNew(); else e.currentTarget.blur(); } }}
        readOnly={!!previewVersion}
        placeholder={isDraft ? 'Name this agent' : undefined}
        autoFocus={isDraft} // eslint-disable-line jsx-a11y/no-autofocus
        className="h-8 min-w-0 rounded-md border border-transparent bg-transparent px-2 text-[13px] font-medium text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] hover:border-[color:var(--border-default)] focus:border-[color:var(--border-strong)] focus:outline-none read-only:cursor-default read-only:hover:border-transparent"
        style={{ width: `${Math.max(6, (nameDraft || (isDraft ? 'Name this agent' : '')).length) + 3}ch`, maxWidth: '40vw' }}
      />
      {!isDraft && (
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-[color:var(--text-muted)]">
          <AgentStatusDot status={workflow?.status} />{agentStatusLabel(workflow?.status)}
        </span>
      )}
      {note && <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]" aria-live="polite">{note}</span>}

      <div className="flex-1" />

      {isDraft ? (
        <button type="button" onClick={onSaveNew} disabled={creating} className={primaryButtonClass}>
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}Save
        </button>
      ) : (
        <>
          <Link to={`${base}/agents/${workflowId}/executions`} className={activityToolbarButtonClass}>Runs</Link>
          {previewVersion ? (
            <button type="button" onClick={onExitPreview} className={activityToolbarButtonClass}>
              <X className="h-3.5 w-3.5" />Exit preview
            </button>
          ) : (
            <button type="button" onClick={onToggleHistory} aria-pressed={historyOpen} title="Version history" className={`${activityToolbarButtonClass} ${historyOpen ? pressed : ''}`}>
              <History className="h-3.5 w-3.5" />Version history
            </button>
          )}
          {testActive ? (
            <button type="button" onClick={onStopTest} className={activityToolbarButtonClass}>
              <Square className="h-3 w-3" />Stop test
            </button>
          ) : (
            <button
              type="button"
              onClick={onTest}
              disabled={runningManual || publishing || !hasNodes || !!previewVersion}
              title={previewVersion ? 'Exit preview to test' : !hasNodes ? 'Add a trigger first' : 'Run the draft without publishing'}
              className={`${activityToolbarButtonClass} disabled:opacity-50`}
            >
              {runningManual ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}Test
            </button>
          )}
          <button type="button" onClick={onPublish} disabled={publishing || !!previewVersion} title={blocking ? blocking.message : undefined} className={primaryButtonClass}>
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}{published ? 'Republish' : 'Publish'}
          </button>
        </>
      )}
    </div>
  );
}
